import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Search } from 'lucide-react';
import { phoneControlsApi, messagesApi } from '../services/api';
import './PhoneControlsPage.css';

const normalizePhone = (value) => {
  if (!value) return '';
  let cleaned = String(value).replace(/\D/g, '');
  if (cleaned.startsWith('0')) cleaned = '972' + cleaned.slice(1);
  else if (cleaned.length === 9) cleaned = '972' + cleaned;
  if (!cleaned.startsWith('972')) cleaned = '972' + cleaned.replace(/^972/, '');
  return '+' + cleaned;
};

const PhoneControlsPage = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [controls, setControls] = useState([]);
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const hasAttemptedLoad = useRef(false);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [whitelisted, setWhitelisted] = useState(false);
  const [blacklisted, setBlacklisted] = useState(false);
  const [notes, setNotes] = useState('');
  const [selectFromContact, setSelectFromContact] = useState('');

  // Search states
  const [blacklistSearch, setBlacklistSearch] = useState('');
  const [whitelistSearch, setWhitelistSearch] = useState('');

  const isRTL = useMemo(() => {
    const dir = document?.documentElement?.getAttribute('dir') || '';
    return dir.toLowerCase() === 'rtl' || ['he', 'ar'].includes(String(i18n.language || '').slice(0, 2));
  }, [i18n.language]);

  // Create a phone-to-name lookup map
  const phoneNameMap = useMemo(() => {
    const map = {};
    contacts.forEach(contact => {
      if (contact.phone) {
        const normalized = normalizePhone(contact.phone);
        map[normalized] = contact.name;
        // Also store without +
        map[normalized.replace('+', '')] = contact.name;
      }
    });
    return map;
  }, [contacts]);

  // Helper function to get name for a phone number
  const getNameForPhone = (phone) => {
    if (!phone) return null;
    const normalized = normalizePhone(phone);
    return phoneNameMap[normalized] || phoneNameMap[normalized.replace('+', '')] || null;
  };

  const fetchChakraContacts = useCallback(async () => {
    try {
      const chatsRes = await messagesApi.listChats(100, 1);
      if (chatsRes?.success) {
        const byPhone = new Map();
        (chatsRes.data?.chats || []).forEach((chat) => {
          if (!chat.phone_number || byPhone.has(chat.phone_number)) return;
          byPhone.set(chat.phone_number, {
            name: chat.contact_name || '',
            phone: chat.phone_number,
          });
        });
        setContacts([...byPhone.values()]);
      }
    } catch (chatsErr) {
      console.warn('Failed to load recent contacts from Chakra:', chatsErr);
    }
  }, []);

  const loadAll = async () => {
    // Prevent multiple simultaneous loads
    if (hasAttemptedLoad.current) return;
    hasAttemptedLoad.current = true;
    
    setLoading(true);
    setLoadError(null);
    
    try {
      // Load phone controls and stats
      const [listRes, statsRes] = await Promise.all([
        phoneControlsApi.list().catch(err => ({ success: false, error: err.message })),
        phoneControlsApi.stats().catch(err => ({ success: false, error: err.message })),
      ]);
      
      if (listRes?.success) {
        setControls(listRes.controls || []);
      } else {
        console.warn('Failed to load controls:', listRes?.error);
      }
      
      if (statsRes?.success) {
        setStats(statsRes.stats || null);
      } else {
        console.warn('Failed to load stats:', statsRes?.error);
      }
      
      // Check if both failed
      if (!listRes?.success && !statsRes?.success) {
        setLoadError(t('phoneControls.errors.load', 'Failed to load phone controls'));
      }
      
      // Load recent contacts straight from Chakra chats (freshest first), not the DB.
      await fetchChakraContacts();
    } catch (err) {
      console.error('Failed to load phone controls:', err);
      setLoadError(t('phoneControls.errors.load', 'Failed to load phone controls'));
    } finally {
      setLoading(false);
    }
  };

  const SkeletonCard = () => (
    <div className="pc-skel-card">
      <div className="pc-skel-line short" />
      <div className="pc-skel-line" />
      <div className="pc-skel-line" />
      <div className="pc-skel-line tiny" />
    </div>
  );

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    const interval = window.setInterval(fetchChakraContacts, 15000);
    return () => window.clearInterval(interval);
  }, [fetchChakraContacts]);

  const openCreate = (mode) => {
    setEditing(null);
    setPhoneNumber('');
    setWhitelisted(mode === 'whitelist');
    setBlacklisted(mode === 'blacklist');
    setNotes('');
    setSelectFromContact('');
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setPhoneNumber(item.peer_number || '');
    setWhitelisted(!!item.whitelisted);
    setBlacklisted(!!item.blacklisted);
    setNotes(item.notes || '');
    setSelectFromContact('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!phoneNumber.trim()) {
      toast.error(t('phoneControls.errors.phoneRequired', 'Phone number is required'));
      return;
    }
    try {
      setSaving(true);
      const payload = {
        peer_number: normalizePhone(phoneNumber),
        whitelisted,
        blacklisted,
        notes: notes.trim(),
      };
      const res = await phoneControlsApi.upsert(payload);
      if (res?.success) {
        toast.success(t('common.saved', 'Saved'));
        setShowModal(false);
        hasAttemptedLoad.current = false;
        await loadAll();
      } else {
        toast.error(res?.error || t('phoneControls.errors.save', 'Failed to save'));
      }
    } catch (err) {
      toast.error(err.message || t('phoneControls.errors.save', 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await phoneControlsApi.delete(id);
      if (res?.success) {
        toast.success(t('common.deleted', 'Deleted'));
        setControls((prev) => prev.filter((c) => c.id !== id));
      } else {
        toast.error(res?.error || t('phoneControls.errors.delete', 'Failed to delete'));
      }
    } catch (err) {
      toast.error(err.message || t('phoneControls.errors.delete', 'Failed to delete'));
    }
  };

  // Filter lists
  const blacklistedControls = useMemo(() => {
    return controls.filter((c) => c.blacklisted && (
      !blacklistSearch ||
      c.peer_number.includes(blacklistSearch) ||
      c.notes?.toLowerCase().includes(blacklistSearch.toLowerCase()) ||
      getNameForPhone(c.peer_number)?.toLowerCase().includes(blacklistSearch.toLowerCase())
    ));
  }, [controls, blacklistSearch, phoneNameMap]);

  const whitelistedControls = useMemo(() => {
    return controls.filter((c) => c.whitelisted && (
      !whitelistSearch ||
      c.peer_number.includes(whitelistSearch) ||
      c.notes?.toLowerCase().includes(whitelistSearch.toLowerCase()) ||
      getNameForPhone(c.peer_number)?.toLowerCase().includes(whitelistSearch.toLowerCase())
    ));
  }, [controls, whitelistSearch, phoneNameMap]);

  if (loading) {
    return (
      <div className={`pc-page ${isRTL ? 'rtl' : ''}`}>
        <div className="pc-header">
          <div>
            <h1>{t('phoneControls.title', 'Phone Controls')}</h1>
            <p>{t('phoneControls.subtitle', 'Manage who the bot can or cannot reply to')}</p>
          </div>
        </div>
        <div className="pc-skel-grid">
          {[1,2,3,4,5,6].map((k) => <SkeletonCard key={k} />)}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={`pc-page ${isRTL ? 'rtl' : ''}`}>
        <div className="pc-header">
          <div>
            <h1>{t('phoneControls.title', 'Phone Controls')}</h1>
            <p>{t('phoneControls.subtitle', 'Manage who the bot can or cannot reply to')}</p>
          </div>
        </div>
        <div className="pc-error-state">
          <div className="pc-error-icon">⚠️</div>
          <h2>{t('phoneControls.errors.loadFailed', 'Failed to load phone controls')}</h2>
          <p>{loadError}</p>
          <button 
            className="pc-primary-btn" 
            onClick={() => {
              hasAttemptedLoad.current = false;
              loadAll();
            }}
          >
            {t('common.retry', 'Try Again')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`pc-page ${isRTL ? 'rtl' : ''}`}>
      <div className="pc-header">
        <div>
          <h1>{t('phoneControls.title', 'Phone Controls')}</h1>
          <p>{t('phoneControls.subtitle', 'Manage who the bot can or cannot reply to')}</p>
        </div>
        <button className="pc-primary-btn" onClick={() => openCreate()}>
          {t('phoneControls.add', 'Add control')}
        </button>
      </div>

      {/* Stats */}
      <div className="pc-stats">
        <div className="pc-stat-card">
          <div className="pc-stat-label">{t('phoneControls.stats.total', 'Total controls')}</div>
          <div className="pc-stat-value">{stats?.total_controls ?? '—'}</div>
        </div>
        <div className="pc-stat-card">
          <div className="pc-stat-label">{t('phoneControls.stats.whitelist', 'Whitelisted')}</div>
          <div className="pc-stat-value ok">{stats?.whitelisted_count ?? '—'}</div>
        </div>
        <div className="pc-stat-card">
          <div className="pc-stat-label">{t('phoneControls.stats.blacklist', 'Blacklisted')}</div>
          <div className="pc-stat-value bad">{stats?.blacklisted_count ?? '—'}</div>
        </div>
        <div className="pc-stat-card">
          <div className="pc-stat-label">{t('phoneControls.stats.storeNumber', 'Store number')}</div>
          <div className="pc-stat-value">{stats?.store_number ?? '—'}</div>
        </div>
      </div>

      {/* Recent contacts quick add - Scrollable */}
      {contacts.length > 0 && (
      <div className="pc-card">
        <div className="pc-card-head-sticky">
          <h3>{t('phoneControls.recent', 'Recent contacts')}</h3>
          <span className="pc-tip">{t('phoneControls.recentTip', 'Add people you talked with quickly')}</span>
        </div>
        <div className="pc-contacts-scrollable">
          {contacts.map((c, idx) => (
            <div key={idx} className="pc-contact">
              <div className="pc-contact-name">{c.name || t('phoneControls.unknown', 'Unknown')}</div>
              <div className="pc-contact-phone">{c.phone}</div>
              <div className="pc-contact-actions">
                <button onClick={() => {
                  setPhoneNumber(normalizePhone(c.phone));
                  setWhitelisted(true);
                  setBlacklisted(false);
                  setNotes('');
                  setShowModal(true);
                }}>
                  {t('phoneControls.actions.whitelist', 'Whitelist')}
                </button>
                <button className="danger" onClick={() => {
                  setPhoneNumber(normalizePhone(c.phone));
                  setWhitelisted(false);
                  setBlacklisted(true);
                  setNotes('');
                  setShowModal(true);
                }}>
                  {t('phoneControls.actions.blacklist', 'Blacklist')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Lists */}
      <div className="pc-grid">
        {/* Blacklisted */}
        <div className="pc-card">
          <div className="pc-card-head-sticky">
            <div className="pc-card-head">
              <h3>{t('phoneControls.blacklist', 'Blacklisted')}</h3>
              <button className="pc-ghost-btn" onClick={() => openCreate('blacklist')}>{t('phoneControls.blockNumber', 'Block a number')}</button>
            </div>
            
            {/* Search */}
            <div className="pc-search-box">
              <Search size={16} className="pc-search-icon" />
              <input
                type="text"
                placeholder={t('phoneControls.searchPlaceholder', 'Search by phone or name...')}
                value={blacklistSearch}
                onChange={(e) => setBlacklistSearch(e.target.value)}
                className="pc-search-input"
              />
            </div>
          </div>

          {blacklistedControls.length === 0 ? (
            <div className="pc-empty">{blacklistSearch ? t('phoneControls.noResults', 'No results found') : t('phoneControls.noEntries', 'No entries')}</div>
          ) : (
            <div className="pc-list-scrollable">
              {blacklistedControls.map((c) => {
                const contactName = getNameForPhone(c.peer_number);
                return (
                  <div key={`bl-${c.id}`} className="pc-row">
                    <div className="pc-status bad" />
                    <div className="pc-peer-info">
                      {contactName && <div className="pc-peer-name">{contactName}</div>}
                      <div className="pc-peer">{c.peer_number}</div>
                    </div>
                    <div className="pc-notes">{c.notes || ''}</div>
                    <div className="pc-actions">
                      <button onClick={() => openEdit(c)}>{t('common.edit', 'Edit')}</button>
                      <button className="danger" onClick={() => handleDelete(c.id)}>{t('common.delete', 'Delete')}</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Whitelisted */}
        <div className="pc-card">
          <div className="pc-card-head-sticky">
            <div className="pc-card-head">
              <h3>{t('phoneControls.whitelist', 'Whitelisted')}</h3>
              <button className="pc-ghost-btn" onClick={() => openCreate('whitelist')}>{t('phoneControls.whitelistNumber', 'Whitelist a number')}</button>
            </div>
            
            {/* Search */}
            <div className="pc-search-box">
              <Search size={16} className="pc-search-icon" />
              <input
                type="text"
                placeholder={t('phoneControls.searchPlaceholder', 'Search by phone or name...')}
                value={whitelistSearch}
                onChange={(e) => setWhitelistSearch(e.target.value)}
                className="pc-search-input"
              />
            </div>
          </div>

          {whitelistedControls.length === 0 ? (
            <div className="pc-empty">{whitelistSearch ? t('phoneControls.noResults', 'No results found') : t('phoneControls.noEntries', 'No entries')}</div>
          ) : (
            <div className="pc-list-scrollable">
              {whitelistedControls.map((c) => {
                const contactName = getNameForPhone(c.peer_number);
                return (
                  <div key={`wl-${c.id}`} className="pc-row">
                    <div className="pc-status ok" />
                    <div className="pc-peer-info">
                      {contactName && <div className="pc-peer-name">{contactName}</div>}
                      <div className="pc-peer">{c.peer_number}</div>
                    </div>
                    <div className="pc-notes">{c.notes || ''}</div>
                    <div className="pc-actions">
                      <button onClick={() => openEdit(c)}>{t('common.edit', 'Edit')}</button>
                      <button className="danger" onClick={() => handleDelete(c.id)}>{t('common.delete', 'Delete')}</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="pc-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="pc-modal" onClick={(e) => e.stopPropagation()} style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
            <div className="pc-modal-head">
              <h3>{editing ? t('phoneControls.modal.editTitle', 'Edit control') : t('phoneControls.modal.addTitle', 'Add control')}</h3>
              <button className="pc-ghost-btn" onClick={() => setShowModal(false)}>{t('common.close', 'Close')}</button>
            </div>
            <div className="pc-modal-body">
              <label>{t('phoneControls.modal.phoneLabel', 'Phone number')}</label>
              <div className="pc-input-row">
                <input
                  type="text"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder={t('phoneControls.modal.phonePlaceholder', '+9725********')}
                />
                {contacts.length > 0 && (
                <select
                  value={selectFromContact}
                  onChange={(e) => {
                    setSelectFromContact(e.target.value);
                    if (e.target.value) setPhoneNumber(normalizePhone(e.target.value));
                  }}
                >
                  <option value="">{t('phoneControls.selectContact', 'Select recent')}</option>
                  {contacts.map((c, idx) => (
                    <option key={idx} value={c.phone}>{c.name || c.phone} ({c.phone})</option>
                  ))}
                </select>
                )}
              </div>

              <div className="pc-checkboxes">
                <label>
                  <input
                    type="checkbox"
                    checked={whitelisted}
                    onChange={(e) => {
                      setWhitelisted(e.target.checked);
                      if (e.target.checked) setBlacklisted(false);
                    }}
                  />
                  {t('phoneControls.modal.whitelistLabel', 'Whitelisted')}
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={blacklisted}
                    onChange={(e) => {
                      setBlacklisted(e.target.checked);
                      if (e.target.checked) setWhitelisted(false);
                    }}
                  />
                  {t('phoneControls.modal.blacklistLabel', 'Blacklisted')}
                </label>
              </div>

              <label>{t('phoneControls.modal.notesLabel', 'Notes')}</label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('phoneControls.notesPlaceholder', 'Optional note')}
              />
            </div>
            <div className="pc-modal-actions">
              <button className="pc-ghost-btn" onClick={() => setShowModal(false)}>{t('common.cancel', 'Cancel')}</button>
              <button className="pc-primary-btn" onClick={handleSave} disabled={saving}>
                {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhoneControlsPage;
